import { IBackStory } from "@src/components/Backstory";
import React, { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import { shimmer, toBase64 } from "@src/utils/shimmer";
import DialogModal from "@src/components/Dialog";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import { truncate } from "lodash";
import flowerBackground from "../../../../public/images/bw-flower.jpeg";
import { GetBackStoryQuery } from "@src/@types/api.d";

export type BackstoryType = NonNullable<
  GetBackStoryQuery["getBackStory"]
>[number];

const MonetBackstoryModal: React.FC<{
  story: BackstoryType;
}> = ({ story }) => {
  const title = story?.title || "The Journey to I Do";
  const role = story?.role;

  return (
    <div className="h-[100dvh] w-screen overflow-hidden bg-[#FAF7F5] text-[#401d15] md:h-auto md:max-h-[86vh] md:w-[min(92vw,920px)]">
      <div className="grid h-full md:max-h-[86vh] md:grid-cols-[0.92fr_1.08fr]">
        <div className="relative hidden min-h-[520px] bg-[#E9E3DF] md:block">
          <Image
            placeholder="blur"
            loading="eager"
            layout="fill"
            blurDataURL={`data:image/svg+xml;base64,${toBase64(
              shimmer(700, 900)
            )}`}
            src={story?.image || flowerBackground}
            alt={title}
            className="object-cover object-top"
            sizes="40vw"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1C1820]/25 to-transparent" />
        </div>

        <div className="flex min-h-0 flex-col md:max-h-[86vh]">
          <div className="border-b border-[#228B22]/18 px-6 py-7 md:px-9">
            <h2 className="font-corGaramond uppercase text-4xl font-light leading-[0.98] text-[#401d15]">
              {title}
            </h2>

            <div className="mt-5 flex items-center gap-4">
              <span className="h-px w-14 bg-gradient-to-r from-[#228B22] to-transparent" />
              <span className="h-1.5 w-1.5 rotate-45 bg-[#228B22]" />
            </div>
          </div>

          <ScrollArea.Root className="min-h-0 flex-1">
            <ScrollArea.Viewport className="h-full px-6 py-7 md:max-h-[46vh] md:px-9">
              {story?.image && (
                <div className="relative mb-7 aspect-[4/3] overflow-hidden bg-[#E9E3DF] md:hidden">
                  <Image
                    placeholder="blur"
                    loading="eager"
                    layout="fill"
                    blurDataURL={`data:image/svg+xml;base64,${toBase64(
                      shimmer(700, 520)
                    )}`}
                    src={story.image}
                    alt={title}
                    className="object-cover object-top"
                    sizes="92vw"
                  />
                </div>
              )}
              <article className="whitespace-pre-line font-corGaramond text-[1.24rem] font-light leading-[1.95] text-[#401d15]/68 md:text-[1.34rem]">
                {story?.story}
              </article>
            </ScrollArea.Viewport>
            <ScrollArea.Scrollbar
              className="flex w-2.5 touch-none select-none bg-[#228B22]/10 p-[2px]"
              orientation="vertical"
            >
              <ScrollArea.Thumb className="relative flex-1 rounded-full bg-[#228B22]/45" />
            </ScrollArea.Scrollbar>
          </ScrollArea.Root>
          {role && (
            <p className="mt-auto shrink-0 px-6 py-5 text-right font-jost text-[0.58rem] font-medium uppercase tracking-[0.32em] text-[#228B22] md:px-9">
              {role}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const Backstory: React.FC<IBackStory> = ({ backStoryData }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const stories = useMemo(
    () => backStoryData?.getBackStory ?? [],
    [backStoryData]
  );

  useEffect(() => {
    if (stories.length <= 1 || isModalOpen) return;

    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentSlide((prev) => (prev + 1) % stories.length);
        setIsAnimating(false);
      }, 300);
    }, 7000);

    return () => clearInterval(interval);
  }, [stories.length, isModalOpen]);

  const handleSlideChange = (index: number) => {
    if (index === currentSlide || isAnimating) return;

    setIsAnimating(true);
    setTimeout(() => {
      setCurrentSlide(index);
      setIsAnimating(false);
    }, 300);
  };

  const handlePrevSlide = () => {
    handleSlideChange((currentSlide - 1 + stories.length) % stories.length);
  };

  const handleNextSlide = () => {
    handleSlideChange((currentSlide + 1) % stories.length);
  };

  const currentStory = stories[currentSlide];
  const nextStory = stories[(currentSlide + 1) % stories.length];
  const storyText = currentStory?.story || "Our love story begins here...";
  const storyTitle = currentStory?.title || "Our Story";

  return stories.length > 0 ? (
    <section className="relative w-full overflow-hidden bg-[#FAF7F5] px-6 pb-20 pt-6 md:px-10 md:pb-28 md:pt-8">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_8%,rgba(34,139,34,0.08),transparent_30%),radial-gradient(circle_at_82%_18%,rgba(28,24,32,0.05),transparent_28%)]" />

      <div className="relative mx-auto max-w-6xl">
        <div className="mb-12 text-center md:mb-16">
          <span className="mb-4 block font-jost text-[0.62rem] font-medium uppercase tracking-[0.35em] text-[#228B22]">
            Our Story
          </span>
          <h2 className="font-corGaramond text-[clamp(3rem,7vw,5.4rem)] font-normal leading-[1.02] text-[#401d15]">
            The Journey to{" "}
            <em className="font-light italic text-[#228B22]">I Do</em>
          </h2>
          <div className="mt-5 flex items-center justify-center gap-4">
            <span className="h-px w-[56px] bg-gradient-to-r from-transparent to-[#228B22]" />
            <span className="h-1.5 w-1.5 rotate-45 bg-[#228B22]" />
            <span className="h-px w-[56px] bg-gradient-to-r from-[#228B22] to-transparent" />
          </div>
        </div>

        {currentStory && (
          <div
            className={`grid items-start gap-12 transition-all duration-500 md:grid-cols-[0.95fr_1.05fr] lg:gap-20 ${
              isAnimating
                ? "translate-y-4 opacity-0"
                : "translate-y-0 opacity-100"
            }`}
          >
            <div className="relative pb-16">
              <div className="relative aspect-[3/4] w-[78%] overflow-hidden bg-[#E9E3DF]">
                <Image
                  placeholder="blur"
                  loading="eager"
                  layout="fill"
                  blurDataURL={`data:image/svg+xml;base64,${toBase64(
                    shimmer(700, 900)
                  )}`}
                  src={currentStory?.image || flowerBackground}
                  alt={currentStory?.title || storyTitle}
                  className="object-cover object-top"
                  sizes="(max-width: 768px) 80vw, 38vw"
                />
              </div>

              <div className="absolute bottom-0 right-0 aspect-[3/4] w-[56%] overflow-hidden border-[6px] border-[#FAF7F5] bg-[#E9E3DF] shadow-[0_24px_70px_rgba(28,24,32,0.12)]">
                <Image
                  placeholder="blur"
                  loading="lazy"
                  layout="fill"
                  blurDataURL={`data:image/svg+xml;base64,${toBase64(
                    shimmer(520, 700)
                  )}`}
                  src={
                    nextStory?.image || currentStory?.image || flowerBackground
                  }
                  alt={nextStory?.title || "Story photo"}
                  className="object-cover object-top"
                  sizes="(max-width: 768px) 58vw, 28vw"
                />
              </div>
            </div>

            <div className="max-w-[620px]">
              <div className="mb-7 flex items-center gap-3">
                {/* <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#228B22] font-jost text-[0.6rem] font-medium uppercase text-white">
                  {String(currentSlide + 1).padStart(2, "0")}
                </span> */}
                <span className="font-jost text-lg font-medium uppercase tracking-[0.25em] text-[#228B22]">
                  {storyTitle}
                </span>
              </div>

              <p className="mb-5 font-corGaramond text-[1.2rem] font-light leading-[2] text-[#401d15]/60 md:text-[1.28rem]">
                {truncate(storyText, { length: 680 })}
              </p>

              <blockquote className="my-8 border-l-2 border-[#228B22] py-3 pl-6 font-corGaramond text-[1.55rem] font-normal italic leading-[1.45] text-[#401d15]">
                "Every chapter brought us closer to this one."
              </blockquote>
              <p className="mb-5 font-jost text-sm uppercase tracking-widest text-[#228B22]">
                {currentStory?.role}
              </p>

              <DialogModal
                trigger={
                  <button className="border border-[#401d15] px-8 py-3 font-jost text-[0.58rem] font-normal uppercase tracking-[0.22em] text-[#401d15] transition hover:bg-[#401d15] hover:text-white">
                    Continue Reading
                  </button>
                }
                onOpenChange={setIsModalOpen}
              >
                <MonetBackstoryModal story={currentStory} />
              </DialogModal>
            </div>
          </div>
        )}

        {stories.length > 1 && (
          <div className="mt-14 flex items-center justify-center gap-4">
            <button
              onClick={handlePrevSlide}
              disabled={isAnimating}
              className="flex h-[46px] w-[46px] items-center justify-center rounded-full border border-[#228B22] bg-transparent text-[#228B22] transition-all duration-200 hover:bg-[#228B22] hover:text-[#FAF7F5] hover:scale-105 active:scale-95 disabled:cursor-not-allowed disabled:opacity-30"
              aria-label="Previous story"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M15 18L9 12L15 6"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              onClick={handleNextSlide}
              disabled={isAnimating}
              className="flex h-[46px] w-[46px] items-center justify-center rounded-full border border-[#228B22] bg-transparent text-[#228B22] transition-all duration-200 hover:bg-[#228B22] hover:text-[#FAF7F5] hover:scale-105 active:scale-95 disabled:cursor-not-allowed disabled:opacity-30"
              aria-label="Next story"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M9 18L15 12L9 6"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    </section>
  ) : null;
};

export default Backstory;
